import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material'
import { ClientSummary, FeeClass, Fund, Redemption, StrategyKey } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { SegmentedTabs } from '../../components/SegmentedTabs'
import { parseAmount, validateAccountNumber } from '../../validation'
import { todayIso } from '../jednotky/format'

type Mode = 'ALL' | 'UNITS' | 'AMOUNT'

interface RedemptionRequestDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (redemption: Redemption) => void
}

function optionLabel(client: ClientSummary): string {
  const id = client.personalNumber ?? client.companyId
  return id ? `${client.fullName} (${id})` : client.fullName
}

/** yyyy-MM-dd → d. M. yyyy */
function czDate(iso?: string | null): string {
  if (!iso) return ''
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${Number(d)}. ${Number(m)}. ${y}`
}

/**
 * Žádost o odkup (Michal 14. 10.): zadává se ve Vklady / výběry jako NOVÁ. Po doručení
 * podepsané žádosti ji obsluha potvrdí a výběr jde do FRONTY (15. 10.). Ocení se až
 * při ocenění fondu k jeho Dni ocenění (nejdřív 14 dní po doručení, čl. 8.1 smlouvy),
 * stejně jako se při ocenění přidělují jednotky vkladům.
 */
export function RedemptionRequestDialog({ open, onClose, onCreated }: RedemptionRequestDialogProps) {
  const api = useOpsApi()
  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [options, setOptions] = useState<ClientSummary[]>([])
  const [searching, setSearching] = useState(false)
  const [funds, setFunds] = useState<Fund[]>([])
  const [fundKey, setFundKey] = useState<StrategyKey | ''>('')
  const [feeClass, setFeeClass] = useState<FeeClass | ''>('')
  const [mode, setMode] = useState<Mode>('ALL')
  const [valueText, setValueText] = useState('')
  const [receivedDate, setReceivedDate] = useState(todayIso())
  const [accountNumber, setAccountNumber] = useState('')
  // Výjimka: ručně zadaná sazba výstupního poplatku v % (prázdné = podle smlouvy).
  const [exitFeeText, setExitFeeText] = useState('')
  const [note, setNote] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedClient(null)
    setSearchInput('')
    setOptions([])
    setFundKey('')
    setFeeClass('')
    setMode('ALL')
    setValueText('')
    setReceivedDate(todayIso())
    setAccountNumber('')
    setExitFeeText('')
    setNote('')
    setErrors({})
    setSubmitError(null)
    setSubmitting(false)
    api.listFunds().then(setFunds).catch(() => setFunds([]))
  }, [open, api])

  useEffect(() => {
    if (!open) return
    if (searchInput.trim().length < 2) {
      setOptions([])
      setSearching(false)
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      api
        .searchClients(searchInput)
        .then((found) => {
          if (!cancelled) setOptions(found)
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, api, searchInput])

  const handleSubmit = async () => {
    const found: Record<string, string> = {}
    if (!selectedClient) found.client = 'Vyberte klienta'
    if (!fundKey) found.fund = 'Vyberte fond'
    const value = parseAmount(valueText)
    if (mode !== 'ALL' && (value === null || value <= 0)) {
      found.value = mode === 'UNITS' ? 'Zadejte počet jednotek' : 'Zadejte částku'
    }
    if (!receivedDate) found.receivedDate = 'Zadejte den doručení žádosti'
    const exitFeePct = exitFeeText.trim() ? parseAmount(exitFeeText) : null
    if (exitFeeText.trim() && (exitFeePct === null || exitFeePct < 0 || exitFeePct > 100)) {
      found.exitFee = 'Zadejte sazbu 0 až 100 %, nebo nechte prázdné'
    }
    if (accountNumber.trim()) {
      const accountError = validateAccountNumber(accountNumber)
      if (accountError) found.accountNumber = accountError
    }
    setErrors(found)
    setSubmitError(null)
    if (Object.keys(found).length > 0) return

    setSubmitting(true)
    try {
      const created = await api.createRedemption({
        clientId: selectedClient!.id,
        fundKey: fundKey as StrategyKey,
        feeClass: feeClass || undefined,
        all: mode === 'ALL' ? true : undefined,
        units: mode === 'UNITS' ? value ?? undefined : undefined,
        amount: mode === 'AMOUNT' ? value ?? undefined : undefined,
        receivedDate,
        accountNumber: accountNumber.trim() || undefined,
        exitFeeRate: exitFeePct !== null ? exitFeePct / 100 : undefined,
        note: note.trim() || undefined,
      })
      onCreated(created)
    } catch (err) {
      setSubmitError(apiError(err, 'Žádost o výběr se nepodařilo založit'))
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Žádost o výběr</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <Alert severity="info">
            Žádost se založí jako nová. Až dojde podepsaná žádost, potvrďte ji v seznamu. Pak jde
            do fronty a ocení se k Dni ocenění, nejdřív 14 dní po doručení.
          </Alert>
          <Autocomplete
            size="small"
            options={options}
            value={selectedClient}
            onChange={(_e, v) => setSelectedClient(v)}
            inputValue={searchInput}
            onInputChange={(_e, v) => setSearchInput(v)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            loading={searching}
            disabled={submitting}
            noOptionsText="Žádný klient nenalezen"
            loadingText="Hledám…"
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Klient (jméno, RČ, IČO) *"
                error={Boolean(errors.client)}
                helperText={errors.client}
              />
            )}
          />
          <TextField
            select
            size="small"
            label="Fond *"
            value={fundKey}
            onChange={(e) => setFundKey(e.target.value as StrategyKey)}
            error={Boolean(errors.fund)}
            helperText={errors.fund}
            disabled={submitting}
          >
            {funds.map((f) => (
              <MenuItem key={f.key} value={f.key}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Třída"
            value={feeClass}
            onChange={(e) => setFeeClass(e.target.value as FeeClass | '')}
            helperText="Nechte prázdné, pokud klient drží jen jednu třídu."
            disabled={submitting}
          >
            <MenuItem value="">Podle pozice klienta</MenuItem>
            <MenuItem value="A">A (EB)</MenuItem>
            <MenuItem value="B">B (NORM)</MenuItem>
          </TextField>
          <SegmentedTabs
            items={[
              { value: 'ALL', label: 'Celá držba' },
              { value: 'UNITS', label: 'Jednotky' },
              { value: 'AMOUNT', label: 'Částka' },
            ]}
            value={mode}
            onChange={(v) => setMode(v as Mode)}
          />
          {mode !== 'ALL' && (
            <TextField
              size="small"
              label={mode === 'UNITS' ? 'Počet jednotek *' : 'Částka (Kč) *'}
              value={valueText}
              onChange={(e) => setValueText(e.target.value)}
              error={Boolean(errors.value)}
              helperText={errors.value}
              disabled={submitting}
            />
          )}
          <TextField
            size="small"
            type="date"
            label="Den doručení žádosti *"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(errors.receivedDate)}
            helperText={errors.receivedDate}
            disabled={submitting}
          />
          <TextField
            size="small"
            label="Číslo účtu klienta pro výplatu"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            error={Boolean(errors.accountNumber)}
            helperText={errors.accountNumber}
            disabled={submitting}
          />
          <TextField
            size="small"
            label="Výstupní poplatek ručně (%)"
            value={exitFeeText}
            onChange={(e) => setExitFeeText(e.target.value)}
            error={Boolean(errors.exitFee)}
            helperText={errors.exitFee || 'Jen výjimka. Prázdné = podle smlouvy (podle stáří jednotek, TREASURY bez poplatku).'}
            placeholder="podle smlouvy"
            disabled={submitting}
          />
          <TextField
            size="small"
            label="Poznámka"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            disabled={submitting}
          />
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          Založit žádost
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export { czDate }
